export function checkHeadlineLength(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const MIN_LENGTH = 20;
    const MAX_LENGTH = 70;

    blockEditorContent.forEach((block, index) => {
        if (block?.type === 'headline') {
            const text = block.content.title || ''; // Changed from text to title
            if (text.length < MIN_LENGTH) {
                warnings.push({
                    message: `Headline "${text}" is too short (${text.length} chars). Minimum recommended length is ${MIN_LENGTH} characters.`,
                    category: 'SEO',
                    uuid: block.uuid,
                    severity: 'low'
                });
            } else if (text.length > MAX_LENGTH) {
                warnings.push({
                    message: `Headline "${text}" is too long (${text.length} chars). Maximum recommended length is ${MAX_LENGTH} characters.`,
                    category: 'SEO',
                    uuid: block.uuid,
                    severity: 'low'
                });
            }
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkHeadlinePresence(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: false, warnings: [{ message: 'No content found', category: 'SEO' }] };
    }

    const hasH1 = blockEditorContent.some(block =>
        (block?.type === 'headline' && block?.content?.level === 'h1') ||
        (block?.type === 'text' && block?.content?.text?.includes('<h1>'))
    );

    return {
        isValid: hasH1,
        warnings: hasH1 ? [] : [{ message: 'Page is missing an H1 headline', category: 'SEO', severity: 'low' }]
    };
}

export function checkHeadlineStructure(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const headlines = blockEditorContent.filter(block => block?.type === 'headline');

    // Check for multiple H1s
    const h1Count = headlines.filter(block => block.content.level === 'h1').length;
    if (h1Count > 1) {
        warnings.push({
            message: 'You should only have one H1/Large Headline per page.',
            category: 'SEO',
            severity: 'low'
        });
    }

    // Check heading hierarchy
    const headingLevels = headlines.map(block => {
        const level = block.content.level;
        return { level, uuid: block.uuid, title: block.content.title };
    });

    let expectedLevel = 1;
    headingLevels.forEach((heading, index) => {
        const currentLevel = parseInt(heading.level.replace('h', ''));

        if (currentLevel > expectedLevel + 1) {
            warnings.push({
                message: `Heading hierarchy skipped levels. Found ${heading.level} after h${expectedLevel}. Consider using h${expectedLevel + 1} instead.`,
                category: 'SEO',
                uuid: heading.uuid,
                severity: 'low'
            });
        }

        expectedLevel = Math.max(expectedLevel, currentLevel);
    });

    headlines.forEach((block, index) => {
        if (!block.content.title || block.content.title.trim() === '') {
            warnings.push({
                message: `Empty headline found.`,
                category: 'SEO',
                uuid: block.uuid,
                severity: 'low'
            });
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkButtonLabel(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const buttonTypes = ['button', 'single-button', 'anchor-button'];
    const buttons = blockEditorContent.filter(block => buttonTypes.includes(block?.type));

    buttons.forEach((block, index) => {
        if (!block.content.label || block.content.label.trim() === '') {
            warnings.push({
                message: `Button is missing a label.`,
                category: 'Missing Content',
                uuid: block.uuid
            });
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkButtonLink(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const buttonTypes = ['button', 'single-button', 'anchor-button'];
    const buttons = blockEditorContent.filter(block => buttonTypes.includes(block?.type));

    buttons.forEach((block, index) => {
        if (!block.content.url || block.content.url.trim() === '') {
            warnings.push({
                message: `Button is missing a link.`,
                category: 'Missing Content',
                uuid: block.uuid
            });
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkMissingMedia(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];

    blockEditorContent.forEach((block, index) => {
        if (block.content === null || block.content === undefined) {
            warnings.push({
                message: 'Content is missing.',
                category: 'Missing Content',
                uuid: block.uuid
            });
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

function parseColor(color) {
    if (!color) return null;

    color = color.trim();

    if (color.startsWith('#')) {
        const hex = color.replace('#', '');

        const normalizedHex = hex.length === 3
            ? hex.split('').map(h => h + h).join('')
            : hex;

        const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    }

    const rgbMatch = color.match(/rgba?\(\s*([^,]+),\s*([^,]+),\s*([^,)]+)/i);
    if (rgbMatch) {
        let r = rgbMatch[1].trim();
        let g = rgbMatch[2].trim();
        let b = rgbMatch[3].trim();

        if (r.includes('%')) {
            r = Math.round((parseFloat(r) / 100) * 255);
            g = Math.round((parseFloat(g) / 100) * 255);
            b = Math.round((parseFloat(b) / 100) * 255);
        } else {
            r = parseInt(r);
            g = parseInt(g);
            b = parseInt(b);
        }

        if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
            return { r, g, b };
        }
    }

    const namedColors = {
        'black': { r: 0, g: 0, b: 0 },
        'white': { r: 255, g: 255, b: 255 },
        'red': { r: 255, g: 0, b: 0 },
        'green': { r: 0, g: 128, b: 0 },
        'blue': { r: 0, g: 0, b: 255 },
        'transparent': null
    };

    const lowerColor = color.toLowerCase();
    if (namedColors.hasOwnProperty(lowerColor)) {
        return namedColors[lowerColor];
    }

    console.warn(`⚠️ Could not parse color: "${color}"`);
    return null;
}

function getRelativeLuminance(rgb) {
    if (!rgb) return 0;

    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;

    const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getContrastRatio(color1, color2) {
    const rgb1 = parseColor(color1);
    const rgb2 = parseColor(color2);

    if (!rgb1 || !rgb2) {
        return null;
    }

    const lum1 = getRelativeLuminance(rgb1);
    const lum2 = getRelativeLuminance(rgb2);

    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    const contrast = (brightest + 0.05) / (darkest + 0.05);

    return contrast;
}

function isLargeText(blockType, headlineLevel) {
    if (blockType === 'headline') {
        return headlineLevel === 'h1' || headlineLevel === 'h2' || headlineLevel === 'h3';
    }
    return false;
}

function getInheritedBackgroundColor(blockUuid, blockEditorContent, design) {
    if (!design || !design.layout || !design.layout.rows) {
        return '#ffffff';
    }

    for (const row of design.layout.rows) {
        for (const column of row.columns) {
            if (column.blocks && column.blocks.includes(blockUuid)) {
                if (column.styles?.desktop?.backgroundColor) {
                    return column.styles.desktop.backgroundColor;
                }
                if (column.styles?.mobile?.backgroundColor) {
                    return column.styles.mobile.backgroundColor;
                }

                if (row.styles?.desktop?.backgroundColor) {
                    return row.styles.desktop.backgroundColor;
                }
                if (row.styles?.mobile?.backgroundColor) {
                    return row.styles.mobile.backgroundColor;
                }

                return design.primarybackground || '#ffffff';
            }
        }
    }

    return '#ffffff';
}

function getInheritedTextColor(blockUuid, blockEditorContent, design) {
    if (!design || !design.layout || !design.layout.rows) {
        return '#000000';
    }

    for (const row of design.layout.rows) {
        for (const column of row.columns) {
            if (column.blocks && column.blocks.includes(blockUuid)) {
                if (column.styles?.desktop?.color) {
                    return column.styles.desktop.color;
                }
                if (column.styles?.mobile?.color) {
                    return column.styles.mobile.color;
                }

                if (row.styles?.desktop?.color) {
                    return row.styles.desktop.color;
                }
                if (row.styles?.mobile?.color) {
                    return row.styles.mobile.color;
                }

                return design.primaryforeground || '#000000';
            }
        }
    }

    return '#000000';
}

function calculateRowIndex(blockIndex, blockEditorContent) {
    return blockIndex;
}

function extractInlineTextColors(htmlContent) {
    if (!htmlContent) return [];

    const colors = [];

    const styleRegex = /style\s*=\s*["']([^"']*color:\s*([^;"']+)[^"']*)["']/gi;
    let match;

    while ((match = styleRegex.exec(htmlContent)) !== null) {
        const colorValue = match[2].trim();
        colors.push(colorValue);
    }

    return colors;
}

export function checkWCAGContrast(blockEditorContent, design) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent) || !design) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const WCAG_AA_NORMAL = 4.5;
    const WCAG_AA_LARGE = 3.0;

    blockEditorContent.forEach((block) => {
        if (!block || !block.type || !block.content || !block.uuid) {
            return;
        }

        const blockType = block.type;
        const content = block.content;
        const blockUuid = block.uuid;

        switch (blockType) {
            case 'headline':
                const headlineText = content.title || '';
                if (headlineText.trim()) {
                    const textColor = content.cssColor || getInheritedTextColor(blockUuid, blockEditorContent, design);
                    const backgroundColor = content.cssBackgroundColor || getInheritedBackgroundColor(blockUuid, blockEditorContent, design);

                    const contrast = getContrastRatio(textColor, backgroundColor);
                    const requiredContrast = isLargeText(blockType, content.level) ? WCAG_AA_LARGE : WCAG_AA_NORMAL;

                    if (contrast && contrast < requiredContrast) {
                        warnings.push({
                            message: `Headline "${headlineText.substring(0, 50)}${headlineText.length > 50 ? '...' : ''}" has insufficient contrast (${contrast.toFixed(2)}:1). Required: ${requiredContrast}:1 for ${isLargeText(blockType, content.level) ? 'large' : 'normal'} text.`,
                            category: 'Accessibility',
                            severity: 'medium',
                            uuid: block.uuid
                        });
                    }
                }
                break;

            case 'text':
            case 'text-with-title':
                const textContent = content.text || '';

                if (textContent.trim()) {
                    const inheritedTextColor = getInheritedTextColor(blockUuid, blockEditorContent, design);
                    const inheritedBackgroundColor = getInheritedBackgroundColor(blockUuid, blockEditorContent, design);

                    const blockTextColor = content.cssColor || inheritedTextColor;
                    const blockBackgroundColor = content.cssBackgroundColor || inheritedBackgroundColor;

                    const inheritedContrast = getContrastRatio(blockTextColor, blockBackgroundColor);

                    if (inheritedContrast && inheritedContrast < WCAG_AA_NORMAL) {
                        warnings.push({
                            message: `Text block has insufficient contrast (${inheritedContrast.toFixed(2)}:1). Required: ${WCAG_AA_NORMAL}:1 for normal text.`,
                            category: 'Accessibility',
                            severity: 'medium',
                            uuid: block.uuid
                        });
                    }

                    const inlineColors = extractInlineTextColors(textContent);

                    inlineColors.forEach(inlineColor => {
                        const inlineContrast = getContrastRatio(inlineColor, blockBackgroundColor);

                        if (inlineContrast && inlineContrast < WCAG_AA_NORMAL) {
                            warnings.push({
                                message: `Text contains inline styled text (color: ${inlineColor}) with insufficient contrast (${inlineContrast.toFixed(2)}:1). Required: ${WCAG_AA_NORMAL}:1 for normal text.`,
                                category: 'Accessibility',
                                severity: 'medium',
                                uuid: block.uuid
                            });
                        }
                    });

                } else {
                }
                break;

            case 'button':
            case 'single-button':
            case 'anchor-button':
                const buttonLabel = content.label || '';
                if (buttonLabel.trim()) {
                    let buttonTextColor = content.cssColor;
                    let buttonBgColor = content.cssBackgroundColor;

                    if (!buttonTextColor && !buttonBgColor) {
                        buttonTextColor = design.ctatextcolor;
                        buttonBgColor = design.primarycontrast;
                    } else if (!buttonTextColor) {
                        buttonTextColor = design.ctatextcolor;
                    } else if (!buttonBgColor) {
                        buttonBgColor = design.primarycontrast;
                    }

                    const contrast = getContrastRatio(buttonTextColor, buttonBgColor);

                    if (contrast && contrast < WCAG_AA_NORMAL) {
                        warnings.push({
                            message: `Button "${buttonLabel}" has insufficient contrast (${contrast.toFixed(2)}:1). Required: ${WCAG_AA_NORMAL}:1 for button text.`,
                            category: 'Accessibility',
                            severity: 'medium',
                            uuid: block.uuid
                        });
                    }

                    const contextBgColor = getInheritedBackgroundColor(blockUuid, blockEditorContent, design);
                    const buttonContextContrast = getContrastRatio(buttonBgColor, contextBgColor);

                    if (buttonContextContrast && buttonContextContrast < 3.0) {
                        warnings.push({
                            message: `Button "${buttonLabel}" background has insufficient contrast against its surrounding background (${buttonContextContrast.toFixed(2)}:1). Consider adding a border or adjusting colors.`,
                            category: 'Accessibility',
                            uuid: block.uuid,
                            severity: 'medium',
                        });
                    }
                }
                break;

            case 'lead-capture':
            case 'form':
                const formButtonTextColor = design.ctatextcolor;
                const formButtonBgColor = design.primarycontrast;
                const formContrast = getContrastRatio(formButtonTextColor, formButtonBgColor);

                if (formContrast && formContrast < WCAG_AA_NORMAL) {
                    warnings.push({
                        message: `Form submit button has insufficient contrast (${formContrast.toFixed(2)}:1). Required: ${WCAG_AA_NORMAL}:1.`,
                        category: 'Accessibility',
                        severity: 'medium',
                        uuid: block.uuid
                    });
                }

                const labelTextColor = getInheritedTextColor(blockUuid, blockEditorContent, design);
                const labelBgColor = getInheritedBackgroundColor(blockUuid, blockEditorContent, design);
                const labelContrast = getContrastRatio(labelTextColor, labelBgColor);

                if (labelContrast && labelContrast < WCAG_AA_NORMAL) {
                    warnings.push({
                        message: `Form labels have insufficient contrast (${labelContrast.toFixed(2)}:1). Required: ${WCAG_AA_NORMAL}:1.`,
                        category: 'Accessibility',
                        severity: 'medium',
                        uuid: block.uuid
                    });
                }
                break;
        }
    });

    if (design && (!design.layout || !design.layout.rows)) {
        const primaryContrast = getContrastRatio(design.primaryforeground, design.primarybackground);
        if (primaryContrast && primaryContrast < WCAG_AA_NORMAL) {
            warnings.push({
                message: `Primary text color has insufficient contrast against primary background (${primaryContrast.toFixed(2)}:1). This affects odd-numbered rows.`,
                category: 'Accessibility',
                severity: 'medium',
            });
        }

        const secondaryContrast = getContrastRatio(design.secondaryforeground, design.secondarybackground);
        if (secondaryContrast && secondaryContrast < WCAG_AA_NORMAL) {
            warnings.push({
                message: `Secondary text color has insufficient contrast against secondary background (${secondaryContrast.toFixed(2)}:1). This affects even-numbered rows.`,
                category: 'Accessibility',
                severity: 'medium',
            });
        }

        const ctaContrast = getContrastRatio(design.ctatextcolor, design.primarycontrast);
        if (ctaContrast && ctaContrast < WCAG_AA_NORMAL) {
            warnings.push({
                message: `Call-to-action button text has insufficient contrast (${ctaContrast.toFixed(2)}:1). This affects all default buttons.`,
                category: 'Accessibility',
                severity: 'medium',
            });
        }
    }

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkContentLength(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const textBlocks = blockEditorContent.filter(block =>
        block?.type === 'text' ||
        block?.type === 'text-with-title' ||
        block?.type === 'headline'
    );

    // Calculate total word count
    let totalWordCount = 0;
    let totalCharCount = 0;

    textBlocks.forEach(block => {
        let text = '';
        if (block.type === 'headline') {
            text = block.content.title || '';
        } else {
            text = block.content.text || '';
            // Strip HTML tags for word counting
            text = text.replace(/<[^>]*>/g, ' ');
        }

        const words = text.trim().split(/\s+/).filter(word => word.length > 0);
        totalWordCount += words.length;
        totalCharCount += text.length;
    });

    // SEO content length recommendations
    const MIN_WORDS = 300;
    const IDEAL_MIN_WORDS = 600;
    const IDEAL_MAX_WORDS = 2000;

    if (totalWordCount < MIN_WORDS) {
        warnings.push({
            message: `Page content is too short (${totalWordCount} words). Minimum recommended length is ${MIN_WORDS} words for SEO.`,
            category: 'SEO',
            severity: 'medium'
        });
    } else if (totalWordCount < IDEAL_MIN_WORDS) {
        warnings.push({
            message: `Page content could be longer (${totalWordCount} words). Ideal length is ${IDEAL_MIN_WORDS}-${IDEAL_MAX_WORDS} words for better SEO performance.`,
            category: 'SEO',
            severity: 'low'
        });
    } else if (totalWordCount > IDEAL_MAX_WORDS * 2) {
        warnings.push({
            message: `Page content is very long (${totalWordCount} words). Consider breaking into multiple pages or sections for better user experience.`,
            category: 'SEO',
            severity: 'low'
        });
    }

    return {
        isValid: warnings.length === 0,
        warnings,
        metrics: {
            wordCount: totalWordCount,
            charCount: totalCharCount
        }
    };
}

export function checkImageOptimization(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const imageBlocks = blockEditorContent.filter(block =>
        block?.type === 'image' ||
        block?.type === 'gallery' ||
        block?.type === 'hero-image'
    );

    imageBlocks.forEach(block => {
        // Check for alt text
        if (!block.content.alt || block.content.alt.trim() === '') {
            warnings.push({
                message: `Image is missing alt text. Alt text is crucial for accessibility and SEO.`,
                category: 'SEO',
                uuid: block.uuid,
                severity: 'low'
            });
        } else if (block.content.alt.length > 125) {
            warnings.push({
                message: `Image alt text is too long (${block.content.alt.length} chars). Keep it under 125 characters.`,
                category: 'SEO',
                uuid: block.uuid,
                severity: 'low'
            });
        }

        // Check for descriptive file names
        if (block.content.src) {
            const fileName = block.content.src.split('/').pop();
            if (fileName && /^(image|img|photo|picture)\d*\.(jpg|jpeg|png|gif|webp)$/i.test(fileName)) {
                warnings.push({
                    message: `Image filename "${fileName}" is not descriptive. Use descriptive filenames for better SEO.`,
                    category: 'SEO',
                    uuid: block.uuid,
                    severity: 'low'
                });
            }
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkReadability(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const textBlocks = blockEditorContent.filter(block =>
        block?.type === 'text' || block?.type === 'text-with-title'
    );

    textBlocks.forEach(block => {
        const text = block.content.text || '';
        const plainText = text.replace(/<[^>]*>/g, ' ').trim();

        if (plainText.length === 0) return;

        // Check sentence length
        const sentences = plainText.split(/[.!?]+/).filter(s => s.trim().length > 0);
        const longSentences = sentences.filter(sentence => {
            const words = sentence.trim().split(/\s+/).length;
            return words > 25;
        });

        if (longSentences.length > 0) {
            warnings.push({
                message: `Found ${longSentences.length} sentences over 25 words. Consider breaking long sentences for better readability.`,
                category: 'Readability',
                uuid: block.uuid,
                severity: 'low'
            });
        }

        // Check paragraph length
        const paragraphs = plainText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
        const longParagraphs = paragraphs.filter(paragraph => {
            const words = paragraph.trim().split(/\s+/).length;
            return words > 150;
        });

        if (longParagraphs.length > 0) {
            warnings.push({
                message: `Found ${longParagraphs.length} paragraphs over 150 words. Consider shorter paragraphs for better readability.`,
                category: 'Readability',
                uuid: block.uuid,
                severity: 'low'
            });
        }
    });

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkLinkOptimization(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const allBlocks = blockEditorContent.filter(block =>
        block?.type === 'text' ||
        block?.type === 'text-with-title' ||
        block?.type === 'button' ||
        block?.type === 'single-button' ||
        block?.type === 'anchor-button'
    );

    let totalLinks = 0;
    let externalLinks = 0;
    let internalLinks = 0;

    allBlocks.forEach(block => {
        const content = block.content;

        // Check button links
        if (['button', 'single-button', 'anchor-button'].includes(block.type)) {
            if (content.url) {
                totalLinks++;
                const isExternal = content.url.startsWith('http') && !content.url.includes(window.location.hostname);

                if (isExternal) {
                    externalLinks++;
                    // Check if external links open in new tab
                    if (!content.target || content.target !== '_blank') {
                        warnings.push({
                            message: `External link should open in a new tab for better user experience.`,
                            category: 'SEO',
                            uuid: block.uuid,
                            severity: 'low'
                        });
                    }
                } else {
                    internalLinks++;
                }

                // Check for descriptive link text
                if (content.label && /^(click here|read more|learn more|here|this)$/i.test(content.label.trim())) {
                    warnings.push({
                        message: `Button text "${content.label}" is not descriptive. Use specific, descriptive text for better SEO.`,
                        category: 'SEO',
                        uuid: block.uuid,
                        severity: 'medium'
                    });
                }
            }
        }

        // Check text content for links
        if (content.text) {
            const linkMatches = content.text.match(/<a[^>]*href\s*=\s*["']([^"']*)["'][^>]*>(.*?)<\/a>/gi);

            if (linkMatches) {
                linkMatches.forEach(linkMatch => {
                    totalLinks++;
                    const hrefMatch = linkMatch.match(/href\s*=\s*["']([^"']*)["']/i);
                    const textMatch = linkMatch.match(/>([^<]*)</);

                    if (hrefMatch) {
                        const url = hrefMatch[1];
                        const isExternal = url.startsWith('http') && !url.includes(window.location.hostname);

                        if (isExternal) {
                            externalLinks++;
                        } else {
                            internalLinks++;
                        }
                    }

                    if (textMatch) {
                        const linkText = textMatch[1].trim();
                        if (/^(click here|read more|learn more|here|this)$/i.test(linkText)) {
                            warnings.push({
                                message: `Link text "${linkText}" is not descriptive. Use specific, descriptive anchor text for better SEO.`,
                                category: 'SEO',
                                uuid: block.uuid,
                                severity: 'medium'
                            });
                        }
                    }
                });
            }
        }
    });

    // Check link distribution
    if (totalLinks === 0) {
        warnings.push({
            message: `Page has no links. Consider adding relevant internal and external links to improve SEO and user experience.`,
            category: 'SEO',
            severity: 'medium'
        });
    } else if (internalLinks === 0) {
        warnings.push({
            message: `Page has no internal links. Internal linking helps with SEO and site navigation.`,
            category: 'SEO',
            severity: 'low'
        });
    }

    return {
        isValid: warnings.length === 0,
        warnings,
        metrics: {
            totalLinks,
            internalLinks,
            externalLinks
        }
    };
}

export function checkCallToActionPresence(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const ctaBlocks = blockEditorContent.filter(block =>
        block?.type === 'button' ||
        block?.type === 'single-button' ||
        block?.type === 'anchor-button' ||
        block?.type === 'lead-capture' ||
        block?.type === 'form'
    );

    if (ctaBlocks.length === 0) {
        warnings.push({
            message: `Page has no call-to-action elements. Add buttons or forms to guide user actions and improve conversion.`,
            category: 'SEO',
            severity: 'medium'
        });
    } else if (ctaBlocks.length === 1) {
        warnings.push({
            message: `Page has only one call-to-action. Consider adding multiple CTAs at different points for better conversion.`,
            category: 'SEO',
            severity: 'low'
        });
    }

    return {
        isValid: warnings.length === 0,
        warnings,
        metrics: {
            ctaCount: ctaBlocks.length
        }
    };
}

export function checkContentStructure(blockEditorContent) {
    if (!blockEditorContent || !Array.isArray(blockEditorContent)) {
        return { isValid: true, warnings: [] };
    }

    const warnings = [];
    const blockTypes = blockEditorContent.map(block => block?.type).filter(Boolean);

    const uniqueTypes = new Set(blockTypes);
    if (uniqueTypes.size < 3) {
        warnings.push({
            message: `Page content lacks variety. Consider adding different content types (images, lists, quotes) for better engagement.`,
            category: 'SEO',
            severity: 'low'
        });
    }

    const hasHeadline = blockTypes.includes('headline');
    const hasText = blockTypes.includes('text') || blockTypes.includes('text-with-title');
    const hasCTA = blockTypes.some(type => ['button', 'single-button', 'anchor-button', 'form', 'lead-capture'].includes(type));

    if (!hasHeadline) {
        warnings.push({
            message: `Page is missing headlines. Headlines help structure content and improve SEO.`,
            category: 'SEO',
            severity: 'high'
        });
    }

    if (!hasText) {
        warnings.push({
            message: `Page is missing text content. Text content is essential for SEO and user engagement.`,
            category: 'SEO',
            severity: 'high'
        });
    }

    if (!hasCTA) {
        warnings.push({
            message: `Page is missing call-to-action elements. CTAs help guide users and improve conversion rates.`,
            category: 'SEO',
            severity: 'medium'
        });
    }

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

export function checkMetaInformation(blockEditorContent, pageData = {}) {
    const warnings = [];

    // Check meta title
    if (!pageData.title || pageData.title.trim() === '') {
        warnings.push({
            message: `Page is missing a title tag. Title tags are crucial for SEO.`,
            category: 'SEO',
            severity: 'high'
        });
    } else if (pageData.title.length < 30) {
        warnings.push({
            message: `Page title is too short (${pageData.title.length} chars). Recommended length is 50-60 characters.`,
            category: 'SEO',
            severity: 'low'
        });
    } else if (pageData.title.length > 60) {
        warnings.push({
            message: `Page title is too long (${pageData.title.length} chars). It may be truncated in search results.`,
            category: 'SEO',
            severity: 'low'
        });
    }

    // Check meta description
    if (!pageData.description || pageData.description.trim() === '') {
        warnings.push({
            message: `Page is missing a meta description. Meta descriptions help improve click-through rates from search results and looks better when sharing on social media.`,
            category: 'SEO',
            severity: 'high'
        });
    } else if (pageData.description.length < 120) {
        warnings.push({
            message: `Meta description is too short (${pageData.description.length} chars). Recommended length is 150-160 characters.`,
            category: 'SEO',
            severity: 'low'
        });
    } else if (pageData.description.length > 160) {
        warnings.push({
            message: `Meta description is too long (${pageData.description.length} chars). It may be truncated in search results.`,
            category: 'SEO',
            severity: 'low'
        });
    }

    return {
        isValid: warnings.length === 0,
        warnings
    };
}

// Comprehensive SEO checker that runs all checks
export function runComprehensiveSEOCheck(blockEditorContent, design, pageData = {}, level = 'comprehensive') {
    const allWarnings = [];
    const metrics = {};

    try {
        // Core SEO checks (always run)
        const headlinePresenceCheck = checkHeadlinePresence(blockEditorContent);
        allWarnings.push(...headlinePresenceCheck.warnings);

        const headlineStructureCheck = checkHeadlineStructure(blockEditorContent);
        allWarnings.push(...headlineStructureCheck.warnings);

        const headlineLengthCheck = checkHeadlineLength(blockEditorContent);
        allWarnings.push(...headlineLengthCheck.warnings);

        const contentLengthCheck = checkContentLength(blockEditorContent);
        allWarnings.push(...contentLengthCheck.warnings);
        if (contentLengthCheck.metrics) {
            Object.assign(metrics, contentLengthCheck.metrics);
        }

        const contentStructureCheck = checkContentStructure(blockEditorContent);
        allWarnings.push(...contentStructureCheck.warnings);

        const metaCheck = checkMetaInformation(blockEditorContent, pageData);
        allWarnings.push(...metaCheck.warnings);

        // Intermediate checks
        if (level === 'intermediate' || level === 'comprehensive') {
            const imageOptCheck = checkImageOptimization(blockEditorContent);
            allWarnings.push(...imageOptCheck.warnings);

            const ctaCheck = checkCallToActionPresence(blockEditorContent);
            allWarnings.push(...ctaCheck.warnings);
            if (ctaCheck.metrics) {
                Object.assign(metrics, ctaCheck.metrics);
            }
        }

        // Advanced checks
        if (level === 'comprehensive') {
            const readabilityCheck = checkReadability(blockEditorContent);
            allWarnings.push(...readabilityCheck.warnings);

            const linkOptCheck = checkLinkOptimization(blockEditorContent);
            allWarnings.push(...linkOptCheck.warnings);
            if (linkOptCheck.metrics) {
                Object.assign(metrics, linkOptCheck.metrics);
            }
        }

        // Categorize warnings by severity
        const criticalWarnings = allWarnings.filter(w => w.severity === 'high');
        const mediumWarnings = allWarnings.filter(w => w.severity === 'medium');
        const lowWarnings = allWarnings.filter(w => w.severity === 'low' || !w.severity);

        return {
            isValid: criticalWarnings.length === 0,
            warnings: allWarnings,
            metrics,
            summary: {
                total: allWarnings.length,
                critical: criticalWarnings.length,
                medium: mediumWarnings.length,
                low: lowWarnings.length
            }
        };
    } catch (error) {
        console.error('Error running comprehensive SEO check:', error);
        return {
            isValid: false,
            warnings: [{
                message: 'Error occurred while running SEO checks. Please try again.',
                category: 'System Error',
                severity: 'high'
            }],
            metrics: {},
            summary: { total: 1, critical: 1, medium: 0, low: 0 }
        };
    }
}


